import React, {useState, useEffect, useCallback} from 'react'
import { connect, useDispatch } from 'react-redux';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { Link } from "react-router-dom";
import {
    getLettreCleAction,
    startLoader,
    createLettreCleAction,
    importLettreCleFileAction,
    deleteDataAction,
    updateLettreCleAction,
    downloadLettreCleTemplateAction
} from '../../../../store/actions/ProviderActions';
import swal from 'sweetalert';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Button as PButton } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { DataTableBase } from '../../DataTables/DataTableBase';
import { currentUser } from "../../../../store/selectors/CurrentUserSelector";
import { useTranslation } from "react-i18next";
// import './LettreCle.css';

const LettreCle = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    
    // Initial states
    const initialFormState = {
        code: '',
        descriptionFr: '',
        descriptionEn: ''
    };
    
    const initialErrorState = { code: '', description: '' };

    // States
    const [formData, setFormData] = useState(initialFormState);
    const [file, setFile] = useState(null);
    const [errors, setErrors] = useState(initialErrorState);
    const [lettreCleId, setLettreCleId] = useState('');
    const [addCard, setAddCard] = useState(false);
    const [updateMode, setUpdateMode] = useState(false);

    // Filter states
    const [filters, setFilters] = useState(null);
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    // Helper to update form data
    const updateFormField = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    }, []);

    // Initialize filters
    const initFilters = useCallback(() => {
        setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            code: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            descriptionFr: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
            createdAt: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
        });
        setGlobalFilterValue("");
    }, []);

    // Reset form
    const resetForm = useCallback(() => {
        setFormData(initialFormState);
        setFile(null);
        setErrors(initialErrorState);
        setLettreCleId('');
        setUpdateMode(false);
    }, [initialFormState, initialErrorState]);

    // Create mode
    const createMode = useCallback(() => {
        resetForm();
        setAddCard(true);
    }, [resetForm]);

    // Download template handler
    const handleDownloadTemplate = useCallback(() => {
        dispatch(downloadLettreCleTemplateAction());
    }, [dispatch]);

    // Render header for data table
    const renderHeader = useCallback(() => {
        return (
            <div className="d-flex justify-content-between align-items-center">
                <PButton 
                    type="button" 
                    icon="pi pi-filter-slash"  
                    label={t('common.reinit')} 
                    outlined 
                    onClick={clearFilter}
                    className="me-2"
                    size="small"
                />
                <div className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        value={globalFilterValue} 
                        onChange={onGlobalFilterChange} 
                        placeholder={t('common.search')}
                        size="small"
                    />
                </div>
            </div>
        );
    }, [globalFilterValue, t]);

    // Clear filter
    const clearFilter = useCallback(() => {
        initFilters();
    }, [initFilters]);

    // Define columns
    const setColumns = useCallback(() => {
        return [
            {name: 'id', headerName: "ID", style: { minWidth: '8rem' }, filter: false, bodyTemplate: null},
            {name: 'code', headerName: "Code", filterPlaceholder: t("common.code-filter"), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'descriptionFr', headerName: "Description", filterPlaceholder: t('provider.desc-filter'), style: { minWidth: '12rem' }, filter: true, bodyTemplate: null},
            {name: 'actions', headerName: 'Actions', filterPlaceholder: '', style: { minWidth: '6rem'}, filter: false, bodyTemplate: getActionsTemplate},
        ]
    }, [t]);

    // Global filter change handler
    const onGlobalFilterChange = useCallback((e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    }, [filters]);

    // Actions template
    const getActionsTemplate = useCallback((rowData) => {
        return (
            <div className="d-flex justify-content-center gap-1">
                {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'UPDATELETTRECLE') && (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => updateData(rowData)}
                        title={t("common.edit")}
                        data-testid={'update-button-'+rowData.id}
                    >
                        <i className="fa fa-pencil"></i>
                    </button>
                )}
                {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'DELETELETTRECLE') && (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(rowData)}
                        title={t("common.delete")}
                        data-testid={'delete-button-'+rowData.id}
                    >
                        <i className="fa fa-trash"></i>
                    </button>
                )}
            </div>
        );
    }, [t]);

    // Delete handler
    const handleDelete = useCallback((rowData) => {
        swal({
            title: t("provider.delLettreCle") + rowData.code + " ?",
            text: t('patient.delete-key'),
            icon: "warning",
            buttons: true,
            dangerMode: true,
        }).then((willDelete) => {
            if (willDelete) {
                const data = { type: "LETTRECLE", id: rowData.id };
                dispatch(startLoader());
                dispatch(deleteDataAction(data));
            }
        });
    }, [t, dispatch]);

    // Update data handler
    const updateData = useCallback((data) => {
        setFormData({
            code: data.code || '',
            descriptionFr: data.descriptionFr || '',
            descriptionEn: data.descriptionEn || ''
        });
        setLettreCleId(data.id);
        setUpdateMode(true);
        setAddCard(true);
    }, []);

    // Permission checks
    const canDo = useCallback((menuCode, actionCode) => {
        const theMenu = props.currentUser.menus?.find(menu => menu.code === menuCode);
        return theMenu ? theMenu.actionMenus?.some(action => action.code === actionCode) : false;
    }, [props.currentUser.menus]);
    
    const hasMenu = useCallback((menuCode) => {
        return props.currentUser.menus?.some(menu => menu.code === menuCode) || false;
    }, [props.currentUser.menus]);

    // File selection handler
    const selectFile = useCallback((event) => {
        setFile(event.target.files[0]);
    }, []);

    // Save handler
    const onLettreCleSave = useCallback((e) => {
        e.preventDefault();
        
        // Handle file upload
        if(file) {
            dispatch(startLoader());
            dispatch(importLettreCleFileAction(file));
            return;
        }
        
        // Validate form
        let error = false;
        const errorObj = { ...initialErrorState };
        
        if (!formData.code.trim()) {
            errorObj.code = t("common.code-required");
            error = true;
        }
        
        if (!formData.descriptionFr.trim()) {
            errorObj.description = t("common.description-required");
            error = true;
        }
        
        setErrors(errorObj);
        
        if (error) {
            swal('Oops', t('common.check-form-fields'), "error");
            return;
        }
        
        dispatch(startLoader());
        
        if (updateMode) {
            const obj = { 
                code: formData.code,
                descriptionFr: formData.descriptionFr,
                descriptionEn: formData.descriptionEn || formData.descriptionFr
            };
            dispatch(updateLettreCleAction(lettreCleId, obj));
        } else {
            const obj = { 
                code: formData.code,
                descriptionFr: formData.descriptionFr,
                descriptionEn: formData.descriptionEn || formData.descriptionFr
            };
            dispatch(createLettreCleAction(obj));
        }
        
        setAddCard(false);
    }, [dispatch, file, formData, initialErrorState, t, lettreCleId, updateMode]);

    // Load data on component mount
    useEffect(() => {
        initFilters();
        dispatch(startLoader());
        dispatch(getLettreCleAction());    
    }, [dispatch, initFilters]);

    // Reset form when lettreCles changes
    useEffect(() => {
        resetForm();
        setAddCard(false);
    }, [props.successMessage]);

    return (
        <>
            {/* Header modernisé */}
            <div className="provider-type-header mb-0 form-head bg-white p-3 bt-5 border-top-solid">
                <div>
                    <h4 className="mb-1">{t("provider.key-letters")}</h4>
                    <p className="text-muted mb-0 small">{t("common.manage-key-letters")}</p>
                </div>
                <div className="d-flex gap-2">
                    {/* Download Template Button */}
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={handleDownloadTemplate}
                    >
                        <i className="fa fa-download me-1"></i> {t("common.download-template")}
                    </button>
                    
                    {/* Create Button */}
                    {hasMenu('MENPARAMACTES') && canDo('MENPARAMACTES', 'CREATELETTRECLE') && (
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={createMode}
                        >
                            <i className="fa fa-plus-circle me-1"></i> {t("provider.new-letter-key")}
                        </button>
                    )}
                </div>
            </div>
            
            {/* Modal modernisée */}
            <Modal 
                show={addCard} 
                onHide={() => setAddCard(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="border-bottom">
                    <Modal.Title className="h5">
                        {updateMode ? t("provider.edit-letter-key") : t("provider.new-letter-key")}
                    </Modal.Title>
                </Modal.Header>
                
                <Modal.Body className="p-4">
                    <form>
                        <div className="mb-3">
                            <label className="form-label">{t("provider.letter-key")} *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                                placeholder="K"
                                value={formData.code}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('code', e.target.value)}
                                maxLength={10}
                            />
                            {errors.code && <div className="invalid-feedback">{errors.code}</div>}
                        </div>
                        
                        <div className="mb-3">
                            <label className="form-label">Description *</label>
                            <input
                                type="text"
                                className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                                placeholder="Entrez la description"
                                value={formData.descriptionFr}
                                disabled={props.showLoading || file}
                                onChange={(e) => updateFormField('descriptionFr', e.target.value)}
                            />
                            {errors.description && <div className="invalid-feedback">{errors.description}</div>}
                        </div>

                        <hr className="my-4" />
                        
                        <div className="mb-3">
                            <h6 className="text-primary mb-3">{t("common.import-csv-key")}</h6>
                            <div className="input-group">
                                <span className="input-group-text">CSV</span>
                                <input 
                                    type="file" 
                                    className="form-control" 
                                    onChange={selectFile}
                                    accept=".csv"
                                />
                            </div>
                            {file && (
                                <div className="alert alert-info mt-2 py-2">
                                    <small>{t("common.selected-file")}: <strong>{file.name}</strong></small>
                                </div>
                            )}
                        </div>
                    </form>
                </Modal.Body>
                
                <Modal.Footer className="border-top">
                    <button 
                        type="button" 
                        className="btn btn-outline-secondary"
                        onClick={() => setAddCard(false)}
                        disabled={props.showLoading}
                    >
                        {t("common.cancel")}
                    </button>
                    
                    {props.showLoading ? (
                        <Button variant="primary" disabled>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />
                            <span className="ms-2">{t("common.saving")}</span>
                        </Button>
                    ) : (
                        <button 
                            type="button" 
                            className="btn btn-primary"
                            onClick={onLettreCleSave}
                        >
                            {updateMode ? t("common.update") : t("common.save")}
                        </button>
                    )}
                </Modal.Footer>
            </Modal>
            
            {/* DataTable */}
            <div className="card border-0 shadow-sm bg-white">
                <div className="card-body p-0">
                    <DataTableBase 
                        data={props.lettreCles.length > 0 ? props.lettreCles : []}
                        emptyMessage={t("common.no-key")}
                        filters={filters}
                        globalFiltersFields={['id', 'code', 'descriptionFr']}
                        header={renderHeader()}
                        columns={setColumns()}
                        exportColumns={[
                            { title: 'ID', dataKey: 'id' },
                            { title: 'Code', dataKey: 'code' },
                            { title: 'Description', dataKey: 'descriptionFr' },
                        ]}
                        rows={10} 
                        showExport={true}
                        exportFilename={"lettres-clés"}
                        loading={props.showLoading}
                        lazy={false}
                    />
                </div>
            </div>
        </>
    );
};

const mapStateToProps = (state) => {
    return {
        lettreCles: state.provider.lettreCles,
        errorMessage: state.clientAuth.errorMessage,
        successMessage: state.clientAuth.successMessage,
        showLoading: state.provider.showLoading,
        currentUser: currentUser(state)
    };
};

export default connect(mapStateToProps)(LettreCle);